/**
 * Session handling, ported from the Nuxt console's AuthService.
 *
 * The platform keeps an httpOnly session cookie on the Livepush domain; this exchanges it
 * for a short-lived bearer token that every console request carries. The token lives in
 * localStorage so a reload does not need a round trip, and is dropped the moment the API
 * says it is stale.
 */

import { consoleConfig } from './config.ts';

let inFlight: Promise<string | null> | null = null;

export function storedToken(): string | null {
  try {
    return localStorage.getItem(consoleConfig().tokenKey);
  } catch {
    return null;
  }
}

function storeToken(token: string): void {
  try {
    localStorage.setItem(consoleConfig().tokenKey, token);
  } catch {
    /* storage blocked — the token still works for this page view */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(consoleConfig().tokenKey);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Trades the session cookie for an access token. Concurrent callers share one request so a
 * page that fires several API calls at once cannot stampede the token endpoint.
 */
export function refreshToken(): Promise<string | null> {
  if (inFlight) return inFlight;

  const { authApiBase } = consoleConfig();
  inFlight = (async () => {
    try {
      const response = await fetch(`${authApiBase}/auth/access_token`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;

      const body = (await response.json()) as { accessToken?: string };
      if (!body.accessToken) return null;

      storeToken(body.accessToken);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function loginUrl(returnTo = window.location.href): string {
  return `${consoleConfig().loginUrl}?redirect=${encodeURIComponent(returnTo)}`;
}

export function redirectToLogin(): void {
  window.location.href = loginUrl();
}

export function signOut(): void {
  clearToken();
  const { logoutUrl } = consoleConfig();
  window.location.href = `${logoutUrl}?referer=${encodeURIComponent(window.location.href)}`;
}

/**
 * Resolves a usable token or sends the visitor to sign in. Returning `null` means a
 * redirect is already under way and the caller should stop rendering.
 */
export async function requireSession(): Promise<string | null> {
  const existing = storedToken();
  if (existing) return existing;

  const fresh = await refreshToken();
  if (fresh) return fresh;

  redirectToLogin();
  return null;
}
